import { type ComponentProps } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, fontFamily, radii, spacing, type } from '~/ui/tokens';

interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  count?: number;
  icon?: ComponentProps<typeof Ionicons>['name'];
  /** Render the label white for use on a coloured (Punch Pink) container. */
  onColor?: boolean;
  compact?: boolean;
}

export function Checkbox({ label, checked, onChange, count, icon, onColor, compact }: CheckboxProps) {
  return (
    <Pressable
      accessibilityRole="checkbox"
      accessibilityState={{ checked }}
      accessibilityLabel={label}
      style={({ pressed }) => [styles.wrap, pressed && styles.wrapPressed]}
      onPress={() => onChange(!checked)}
    >
      <View style={[styles.box, checked && styles.boxChecked]}>
        {checked ? <Ionicons name="checkmark" size={12} color={colors.white} /> : null}
      </View>
      {icon ? <Ionicons name={icon} size={16} color={checked ? colors.brand : colors.inkSoft} /> : null}
      <Text style={[styles.label, compact && styles.labelCompact, onColor && styles.labelOnColor]}>{label}</Text>
      {count !== undefined ? <Text style={styles.count}>{count}</Text> : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  wrap: {
    minHeight: 28,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(1),
  },
  wrapPressed: { opacity: 0.68 },
  box: {
    width: 18,
    height: 18,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: colors.line,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.card,
  },
  boxChecked: {
    borderColor: colors.brand,
    backgroundColor: colors.brand,
  },
  label: {
    flex: 1,
    fontFamily: fontFamily.medium,
    ...type.bodyRegular,
    color: colors.inkSoft,
  },
  labelOnColor: {
    color: colors.white,
  },
  labelCompact: {
    fontSize: 13,
    lineHeight: 18,
  },
  count: {
    fontFamily: fontFamily.regular,
    ...type.metadata,
    color: colors.inkFaint,
    fontVariant: ['tabular-nums'],
  },
});
